import React, { useState } from "react";
import { useViewport } from "App/common/useViewport";
import { AppRequiredDialog } from "./AppRequiredDialog";

/**
 * Below M, mobile web can't compose/comment — those actions belong in the app.
 * Centralizes the "intercept the tap, show the AppRequiredDialog" behavior the
 * comment input, chat input, and comment card each rolled by hand.
 *
 * Two ways to consume it:
 *  - Spread `clickCaptureProps` onto an element so a below-M tap opens the
 *    dialog instead of its normal action (capture phase, so it preempts inner
 *    controls). The emitted `data-mobile-prompt` attr lets the input rows keep
 *    their `> * { pointer-events: none }` rule that stops the keyboard flash.
 *  - Call `openAppRequiredDialog()` from a real button's onClick (the Home
 *    header), branching on `isMobile`.
 * Render `appRequiredDialogComponent` once in the same subtree either way.
 */
export const useAppRequiredDialog = () => {
  const { isMobile } = useViewport();
  const [isOpen, setIsOpen] = useState(false);

  const clickCaptureProps = isMobile
    ? {
        // Capture phase + stopPropagation preempts inner controls; in React's
        // synthetic system it also stops the same element's bubble onClick.
        onClickCapture: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
        },
        "data-mobile-prompt": true,
      }
    : {};

  const appRequiredDialogComponent = (
    <AppRequiredDialog open={isOpen} onRequestClose={() => setIsOpen(false)} />
  );

  return {
    isMobile,
    clickCaptureProps,
    openAppRequiredDialog: () => setIsOpen(true),
    appRequiredDialogComponent,
  };
};
